import * as THREE from 'three';
import {
  createGlowMaterial,
  createMatteMaterial,
  palette
} from '../../visual';
import { getShopDefinition } from './ShopKind';
import type { ShopDefinition, ShopKind } from './ShopKind';

export interface StationCounterOptions {
  readonly kind: ShopKind;
  readonly position: THREE.Vector3;
  readonly orientation: number;
}

/**
 * Counter = the physical stand the player walks up to.
 * The vendor NPC stands behind the counter; the player stands in front of it.
 */
export class StationCounter {
  readonly object3D: THREE.Group;
  readonly kind: ShopKind;
  readonly definition: ShopDefinition;
  readonly interactionPoint = new THREE.Vector3();
  readonly vendorAnchor = new THREE.Vector3();
  readonly forward = new THREE.Vector3();
  readonly right = new THREE.Vector3();
  readonly interactionRadius = 3.8;

  private readonly signMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly beacon: THREE.Mesh;
  private time = Math.random() * 10;

  constructor(options: StationCounterOptions) {
    this.kind = options.kind;
    this.definition = getShopDefinition(options.kind);

    this.object3D = new THREE.Group();
    this.object3D.name = `counter:${options.kind}`;
    this.object3D.position.copy(options.position);
    this.object3D.rotation.y = options.orientation;

    this.forward.set(
      Math.sin(options.orientation),
      0,
      Math.cos(options.orientation)
    );
    this.right.set(
      Math.cos(options.orientation),
      0,
      -Math.sin(options.orientation)
    );

    this.beacon = this.build();

    // Interaction point: in front of the counter (on the -forward side,
    // where the player walks up to the counter).
    this.interactionPoint
      .copy(options.position)
      .addScaledVector(this.forward, -2.1);
    // Vendor anchor: behind the counter.
    this.vendorAnchor
      .copy(options.position)
      .addScaledVector(this.forward, 1.2);
  }

  update(dt: number): void {
    this.time += dt;
    const pulse = 0.6 + Math.sin(this.time * 1.8) * 0.25;
    for (const mat of this.signMaterials) {
      mat.opacity = pulse;
    }
    const beaconPulse = 0.85 + Math.sin(this.time * 2.4) * 0.15;
    this.beacon.scale.setScalar(beaconPulse);
  }

  getWorldInteractionPoint(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.interactionPoint);
  }

  private build(): THREE.Mesh {
    const accent = this.definition.accent;
    const accentGlow = this.definition.accentGlow;

    const hullMat = createMatteMaterial({
      color: palette.station.hull,
      roughness: 0.55
    });
    const darkMat = createMatteMaterial({
      color: palette.station.hullDark,
      roughness: 0.8
    });
    const trimMat = createMatteMaterial({
      color: palette.station.trim,
      roughness: 0.7
    });
    const accentMat = createMatteMaterial({
      color: accent,
      roughness: 0.3,
      emissive: accent,
      emissiveIntensity: 0.35
    });
    const glowMat = createGlowMaterial(accentGlow, 0.95, true);

    // Base platform (raised dais).
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(2.8, 3.2, 0.2, 32),
      darkMat
    );
    this.object3D.add(base);

    const baseRing = new THREE.Mesh(
      new THREE.TorusGeometry(2.95, 0.04, 8, 48),
      accentMat
    );
    baseRing.rotation.x = Math.PI / 2;
    baseRing.position.y = 0.11;
    this.object3D.add(baseRing);

    // Counter body — a sleek L-shaped desk facing the player.
    const counterTop = new THREE.Mesh(
      new THREE.BoxGeometry(3.2, 0.12, 1.1),
      hullMat
    );
    counterTop.position.set(0, 1.05, 0.3);
    this.object3D.add(counterTop);

    const counterFront = new THREE.Mesh(
      new THREE.BoxGeometry(3.2, 1.0, 0.14),
      trimMat
    );
    counterFront.position.set(0, 0.5, -0.25);
    this.object3D.add(counterFront);

    const counterGlowStrip = new THREE.Mesh(
      new THREE.BoxGeometry(3.0, 0.1, 0.04),
      accentMat
    );
    counterGlowStrip.position.set(0, 0.16, -0.34);
    this.object3D.add(counterGlowStrip);

    const counterSide = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 1.0, 1.2),
      trimMat
    );
    counterSide.position.set(-1.55, 0.5, 0.3);
    this.object3D.add(counterSide);

    const counterSideR = counterSide.clone();
    counterSideR.position.x = 1.55;
    this.object3D.add(counterSideR);

    // Holographic sign above the counter — visible to anyone walking up
    // to the counter from the front. The backing box is the dark plate;
    // the glow sits just on top of its front face (toward the player),
    // and a text plane just in front of that carries the shop name.
    const signBacking = new THREE.Mesh(
      new THREE.BoxGeometry(3.0, 0.7, 0.08),
      darkMat
    );
    signBacking.position.set(0, 3.2, 0.9);
    this.object3D.add(signBacking);

    // Subtle accent rim around the backing — sells it as a holographic
    // panel rather than a flat sticker.
    const signFrame = new THREE.Mesh(
      new THREE.BoxGeometry(3.05, 0.75, 0.04),
      accentMat
    );
    signFrame.position.set(0, 3.2, 0.855);
    this.object3D.add(signFrame);

    // Glow plane — `Math.PI` Y rotation flips the face so it points
    // toward the player. Slightly in front of the backing's front face
    // (z = 0.86) so the glow actually shows. The pulse animation lives
    // on this material via `signMaterials`.
    const signGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(2.85, 0.55),
      glowMat
    );
    signGlow.position.set(0, 3.2, 0.835);
    signGlow.rotation.y = Math.PI;
    this.object3D.add(signGlow);
    this.signMaterials.push(signGlow.material as THREE.MeshBasicMaterial);

    // Text plane — sits a hair in front of the glow with the shop name
    // baked into a CanvasTexture. Kept off the pulse list so it stays
    // crisp + readable while the glow does the breathing animation.
    const textTexture = createSignTexture(this.definition.signText, accent);
    const textMat = new THREE.MeshBasicMaterial({
      map: textTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false
    });
    const signText = new THREE.Mesh(
      new THREE.PlaneGeometry(2.8, 0.5),
      textMat
    );
    signText.position.set(0, 3.2, 0.83);
    signText.rotation.y = Math.PI;
    this.object3D.add(signText);

    // Back pillars supporting the sign.
    const pillarGeo = new THREE.BoxGeometry(0.18, 3.5, 0.18);
    const pillarL = new THREE.Mesh(pillarGeo, trimMat);
    pillarL.position.set(-1.45, 1.75, 0.9);
    this.object3D.add(pillarL);
    const pillarR = pillarL.clone();
    pillarR.position.x = 1.45;
    this.object3D.add(pillarR);

    // Decorative arch behind the counter framing the vendor.
    const archGeo = new THREE.TorusGeometry(1.9, 0.08, 10, 32, Math.PI);
    const arch = new THREE.Mesh(archGeo, accentMat);
    arch.rotation.x = Math.PI;
    arch.rotation.y = 0;
    arch.position.set(0, 2.8, 1.2);
    this.object3D.add(arch);

    // Floor emissive marker for interaction zone.
    const marker = new THREE.Mesh(
      new THREE.RingGeometry(1.1, 1.3, 40),
      glowMat
    );
    marker.rotation.x = -Math.PI / 2;
    marker.position.set(0, 0.11, -2.0);
    this.object3D.add(marker);

    // Side beacon.
    const beacon = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 14, 10),
      glowMat
    );
    beacon.position.set(0, 3.6, 0.9);
    this.object3D.add(beacon);

    return beacon;
  }
}

/**
 * Render a station-counter sign label into a CanvasTexture. Big bold
 * tracked letters with a soft accent-coloured glow so the panel reads
 * "holographic shop sign" from across the hangar.
 *
 * `accent` is the shop definition's accent (a `0xRRGGBB` number); we
 * convert it once for the canvas shadow / outer-glow strokes.
 */
function createSignTexture(text: string, accent: number): THREE.CanvasTexture {
  const width = 1024;
  const height = 256;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Defensive — every browser we ship to has 2D context, but typing
    // narrows to non-null below.
    return new THREE.CanvasTexture(canvas);
  }

  ctx.clearRect(0, 0, width, height);

  const r = (accent >> 16) & 0xff;
  const g = (accent >> 8) & 0xff;
  const b = accent & 0xff;
  const accentCss = `rgb(${r},${g},${b})`;

  // Stretch the letters horizontally so the label feels like
  // "STATION SIGNAGE" rather than a generic sans-serif name.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font =
    '700 132px "Orbitron", "Eurostile", "Inter", system-ui, "Segoe UI", sans-serif';

  // Manual letter-spacing — `letterSpacing` on canvas is partially
  // supported, so render each glyph individually with a fixed gap.
  const glyphs = text.toUpperCase().split('');
  const trackingPx = 14;
  let totalWidth = 0;
  for (const glyph of glyphs) {
    totalWidth += ctx.measureText(glyph).width + trackingPx;
  }
  totalWidth -= trackingPx;
  let cursor = (width - totalWidth) / 2;

  // Draw a faint outer glow first by stamping the same text multiple
  // times with the shadow blur turned up — gives the label the
  // emissive halo without needing post-processing.
  ctx.shadowColor = accentCss;
  ctx.shadowBlur = 36;
  ctx.fillStyle = accentCss;
  let drawCursor = cursor;
  for (const glyph of glyphs) {
    const w = ctx.measureText(glyph).width;
    ctx.fillText(glyph, drawCursor + w / 2, height / 2);
    drawCursor += w + trackingPx;
  }

  // Crisp white core on top of the glow so the letters read clearly
  // even at oblique viewing angles.
  ctx.shadowColor = accentCss;
  ctx.shadowBlur = 8;
  ctx.fillStyle = '#ffffff';
  drawCursor = cursor;
  for (const glyph of glyphs) {
    const w = ctx.measureText(glyph).width;
    ctx.fillText(glyph, drawCursor + w / 2, height / 2);
    drawCursor += w + trackingPx;
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}
